import React, { useState, useEffect } from 'react';
import { X, User, Heart, Calendar, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { UserProfile } from '../types/invitation';
import { createLogger } from '../lib/logger';

const log = createLogger('user');

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose }) => {
  const { user, userProfile } = useAuth();
  const [formData, setFormData] = useState({
    displayName: '',
    partnerName: '',
    weddingDate: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // 初始化表單資料
  useEffect(() => {
    if (userProfile) {
      setFormData({
        displayName: userProfile.displayName || '',
        partnerName: userProfile.partnerName || '',
        weddingDate: userProfile.weddingDate || ''
      });
    }
  }, [userProfile, isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(''); // 清除錯誤提示
  };

  const handleSave = async () => {
    if (!user?.id) return;
    
    // 基本驗證
    if (!formData.displayName.trim()) {
      setError('顯示名稱不能為空');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const updates: Partial<UserProfile> = {
        displayName: formData.displayName.trim(),
        partnerName: formData.partnerName.trim() || undefined,
        weddingDate: formData.weddingDate || undefined
      };

      // 更新用戶資料
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          display_name: updates.displayName,
          partner_name: updates.partnerName,
          wedding_date: updates.weddingDate
        })
        .eq('user_id', user.id);

      if (updateError) {
        throw updateError;
      }

      onClose(); // 關閉 Modal
    } catch (error) {
      log.error('更新個人資料失敗:', error);
      setError('更新失敗，請稍後再試');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* 背景遮罩 */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-wedding-bg rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-wedding-pink/30">
            <h2 className="text-xl font-semibold text-wedding-text-title flex items-center">
              <User className="h-5 w-5 mr-2 text-wedding-pink" />
              個人資料
            </h2>
            <button
              onClick={handleClose}
              className="text-wedding-text-body hover:text-wedding-pink transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* 顯示名稱 */}
            <div>
              <label className="block text-sm font-medium text-wedding-text-title mb-2">
                顯示名稱 *
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                placeholder="請輸入您的姓名"
                className="w-full px-3 py-2 border border-wedding-pink/30 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-pink focus:border-transparent"
              />
            </div>

            {/* 伴侶姓名 */}
            <div>
              <label className="block text-sm font-medium text-wedding-text-title mb-2 flex items-center">
                <Heart className="h-4 w-4 mr-1 text-wedding-pink" />
                伴侶姓名
              </label>
              <input
                type="text"
                value={formData.partnerName}
                onChange={(e) => handleInputChange('partnerName', e.target.value)}
                placeholder="請輸入伴侶姓名"
                className="w-full px-3 py-2 border border-wedding-pink/30 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-pink focus:border-transparent"
              />
            </div>

            {/* 婚禮日期 */}
            <div>
              <label className="block text-sm font-medium text-wedding-text-title mb-2 flex items-center">
                <Calendar className="h-4 w-4 mr-1 text-wedding-pink" />
                婚禮日期
              </label>
              <input
                type="date"
                value={formData.weddingDate}
                onChange={(e) => handleInputChange('weddingDate', e.target.value)}
                className="w-full px-3 py-2 border border-wedding-pink/30 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-pink focus:border-transparent"
              />
            </div>

            {/* 錯誤訊息 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-6 border-t border-wedding-pink/30">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 border border-wedding-pink/30 text-wedding-text-body rounded-md hover:bg-wedding-pink/5 transition-colors disabled:opacity-50"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !formData.displayName.trim()}
              className="px-4 py-2 bg-wedding-pink text-white rounded-md hover:bg-wedding-pink/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isLoading ? '儲存中...' : '儲存'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfileModal;